import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";
import { bindActionCreators, Dispatch } from 'redux';
import * as NavigationStore from '../store/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome } from '@fortawesome/pro-solid-svg-icons' 
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Spinner from 'react-bootstrap/Spinner';
import { Crumb } from '../types/Crumb';
import { NavigationElementSummaryDto } from '../types/NavigationElementSummaryDto';
import AppSettings from '../types/AppSettings';
import AppRoute from '../types/AppRoute';
import { CommonAppState } from '../store';
import { getFoundationServiceSchemeAndHost } from '../utils/getFoundationServiceSchemeAndHost';

type NavBreadcrumbProps = {
    appSettings: AppSettings;
    location: any;
    navigationState: NavigationStore.NavigationState;
    actions: typeof NavigationStore.actionCreators;
}

interface NavBreadcrumbState {
    routes: Crumb[];
}

class NavBreadcrumb extends React.Component<NavBreadcrumbProps, NavBreadcrumbState> {

    constructor(props: NavBreadcrumbProps){
        super(props);
        
        // Flatten AppRoutes and normalize paths
        let crumbRoutes: Crumb[] = [];
        // crumbRoutes.push({
        //     path: this.normalizePathIdPlaceholder(this.props.appSettings.defaultRoute.path),
        //     label: this.props.appSettings.defaultRoute.name
        // });
        this.props.appSettings.routes.forEach((route: AppRoute) => {
            // Skip the default route
            if(route.path === "/") 
                return;
                
            crumbRoutes.push({
                path: this.normalizePathIdPlaceholder(route.path),
                label: route.name,
                external: false
            });
        });

        this.state = {
            routes: crumbRoutes.sort(this.sortCrumbsOnPath)
        };
    }

    private sortCrumbsOnPath(a: Crumb, b: Crumb) {
        var pathA = a.path.toLowerCase(); // ignore upper and lowercase
        var pathB = b.path.toLowerCase(); // ignore upper and lowercase
        if (pathA < pathB) {
          return -1;
        }
        if (pathA > pathB) {
          return 1;
        }
      
        // names must be equal
        return 0;
    }

    private normalizePathIdPlaceholder(path: string): string {
        return path.replace(/:[^/]+/g, ":id");
    }

    // recursive method to get breadcrumb subcategories
    private getSubCategories(item: NavigationElementSummaryDto, crumbs: Crumb[]){

        if(item.items === null){
            return crumbs;
        }

        let tmp = crumbs;

        let subCategory = item.items.find((item: NavigationElementSummaryDto) => {
            return item.path === this.props.location.pathname;        
        });

        if(subCategory !== undefined){
            tmp.push({path: item.path, label: item.category, external: false});
            this.getSubCategories(subCategory, tmp);
        }

        return tmp;
    }

    // builds the breadcrumb menu
    breadcrumbs = () => {

        const navState = this.props.navigationState;
        let crumbs: Crumb[] = [];

        let pathname = this.props.location.pathname;

        // expression to match unique ids or numeric ids
        let regex = /\b\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b|\/(\d+)/g;

        // replace unique ids and numeric ids in pathname with placeholder value
        pathname = pathname.replace(regex, "/:id");

        if(this.props.appSettings.basePath) {
            // Add the root external link to the foundation service
            var foundationServiceSchemeAndHost = getFoundationServiceSchemeAndHost();
            if(foundationServiceSchemeAndHost !== undefined) {
                crumbs.push({path: foundationServiceSchemeAndHost, label: "Root", external: true});
            } else {
                crumbs.push({path: "/", label: "Root", external: true});
            }
        } else {
            // Add internal link to the Foundation Service
            crumbs.push({path: "/", label: "Root", external: false});
        }


        if(navState.navigation.basePath !== "/"){
            // Build a list of crumbs from the nav
            navState.navigation.nav.forEach((nav: NavigationElementSummaryDto) => {
                let category = nav.items.find((item: NavigationElementSummaryDto) => {
                    return item.path === pathname;
                });

                if(category !== undefined){
                    crumbs.push({path: '#', label: nav.category, external: false});
                }
                else{
                    let sub: Crumb[] = [];
                    nav.items.forEach((item: NavigationElementSummaryDto) => {
                        let tmp = this.getSubCategories(item, []);

                        if(tmp.length > 0){
                            sub = sub.concat(tmp);
                        }
                    });
                    
                    if(sub.length > 0){
                        crumbs.push({path: '#', label: nav.category, external: false});
                        crumbs = crumbs.concat(sub);
                    }
                }
            })

            crumbs.push({path: navState.navigation.basePath, label: navState.navigation.service, external: false});
        }

        const routes = this.state.routes.filter((crumb) => pathname.includes(crumb.path));
        routes.forEach(route => {
            crumbs.push(route);
        });

        // console.debug("Routes", this.props.appSettings.routes);
        // console.debug("Crumbs", crumbs);

        return (
            <React.Fragment>
                {crumbs.map((crumb: Crumb, index: number) => {
                    let active = (index === (crumbs.length - 1) || crumb.path === '#') ? true: false;

                    let renderedPath = crumb.path;

                    if(crumb.path.includes("/:id")){
                        // get all ids from the pathname whenever a crumb path contains the placeholder value /:id
                        // since we are shifting through an array of the ids from the pathname we need to grab this each time 
                        let ids = this.props.location.pathname.match(regex);
                        renderedPath = crumb.path.replace(/\/:id/g, () => ids.shift());
                    }

                    if(index === 0){
                        return (
                            crumb.external 
                                ? <Breadcrumb.Item key={index} href={renderedPath} active={active}><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
                                : <Breadcrumb.Item key={index} linkProps={{ to: renderedPath }} linkAs={Link} active={active}><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
                        );    
                    }

                    if(navState.navigation.basePath !== "/" && navState.navigation.basePath === crumb.path){
                        return (
                            <Breadcrumb.Item key={index} linkProps={{ to: "/" }} linkAs={Link} active={active}>{crumb.label}</Breadcrumb.Item>
                        );
                    }

                    return (
                        <Breadcrumb.Item key={index} linkProps={{ to: renderedPath }} linkAs={Link} active={active}>{crumb.label}</Breadcrumb.Item>
                    );
                })}
            </React.Fragment>
        );
    }

    render(){
        const navState = this.props.navigationState;

        return (
            <React.Fragment>
                <Breadcrumb>
                    {!navState.isLoadedNavigation
                    ?
                    <Spinner animation="border" variant="primary" size="sm" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    :
                    <React.Fragment>
                        <this.breadcrumbs></this.breadcrumbs>
                    </React.Fragment>
                    }
                </Breadcrumb>
            </React.Fragment>
        );
    }
}

function mapStateToProps(state: CommonAppState) {
    return {
        location: state.router.location,
        navigationState: state.navigation
    };
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        actions: bindActionCreators(Object.assign({}, NavigationStore.actionCreators), dispatch )
    }
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(NavBreadcrumb);